import { PostDownloadDto, WikiArticleDownloadDto, WikiCategoryDownloadDto, WikiSearchResultDto } from 'collaboration-service';
import SwitchButton from 'components/Switch/SwitchButton';
import { fadeColor, getColor, Icon, ImgIcons, lightenColor, LottieIcon, styled } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { getRidOfHtml } from './AscsHelpers';
import { AscsArticleMap, RowTypes } from './AscsTypes';
import PlaylistEditPreviewElement from 'views/Playlist/Edit/PlaylistEditPreviewElement';

const CONNECTOR_W = 38;
const BRANCH_ML = 18;
const LEVEL_ORDER: RowTypes[] = ['cat', 'sub', 'art', 'sec', 'col'];

export interface AscsCheckedSelection {
    articleId?: string;
    sectionId?: string;
    columnId?: string;
    categoryId?: string;
    subCategoryId?: string;
}

export interface AscsPreselectedPath {
    categoryId?: string;
    subCategoryId?: string;
    articleId?: string;
    sectionId?: string;
    columnId?: string;
}

const Branch = styled.div`
    border-left: 1px solid ${p => getColor(p, "@middleLightGrey")};
    margin-left: ${BRANCH_ML}px;
`;

const Outer = styled.div<{ active: boolean; isRoot?: boolean }>`
    position: relative;
    padding: 0px 8px 0px ${p => p.isRoot ? 6 : CONNECTOR_W + 6}px;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: background 0.15s ease-in-out;
    background: ${p => p.active ? fadeColor(p, "@accentGreen", 10) : getColor(p, "@mainBackground")};
    color: ${p => p.active ? lightenColor(p, "@accentGreen", 80) : getColor(p, "@darkGrey")};
    user-select: none;
    &:hover {
        background: ${p => p.active ? fadeColor(p, "@accentGreen", 15) : fadeColor(p, "@accentLightBlue", 10)};
    }
    &::before {
        content: ${p => p.isRoot ? 'none' : "''"};
        position: absolute;
        left: -1px;
        top: 50%;
        transform: translateY(-50%);
        width: ${CONNECTOR_W}px;
        height: 2px;
        border-top: 1px solid ${p => getColor(p, "@middleLightGrey")};
        //background: yellow;
    }
`;

const Title = styled.span<{ selected: boolean }>`
    flex: 1;
    cursor: pointer;
    font-weight: ${p => p.selected ? 'bold' : 'normal'};
    display: flex;
    align-items: center;
    gap: 5px;
    position: relative;
`;
const TypeBox = styled.span<{ left: number }>`
    position: absolute;
    left: ${p => p.left}px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;    
    color: ${p => getColor(p, "@lightGrey")};  
    border: 1px solid ${p => getColor(p, "@middleLightGrey")};
    line-height: 12px;
    text-transform: uppercase;
    background: ${p => getColor(p, "@mainBackground")};
    text-align: center;
    width: 24px;
    border-radius: 2px;
`;

const Infos = styled.span<{ loading: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-right: ${p => p.loading ? -2 : 0}px;
    position: relative;
`;



export interface AscsTreeViewProps {
    categories: WikiCategoryDownloadDto[];
    articles: AscsArticleMap;
    articleDetailsCache: Record<string, WikiSearchResultDto>;
    loadingArticleIds: Set<string>;
    loadingSubcatIds: Set<string>;
    contentLanguage?: string;
    selectSection?: boolean;
    selectColumns?: boolean;
    t: (key: string, options?: Record<string, any>) => string;
    onShowSubcats: (catId: string) => void;
    onLoadSubcatArticles: (cat: WikiCategoryDownloadDto) => void;
    onLoadSubcatWithSections: (subCat: WikiCategoryDownloadDto) => void;
    onShowArticle: (article: WikiArticleDownloadDto) => void;
    onSectionSelected: (articleId: string, sectionId: string) => void;
    onColumnSelected: (articleId: string, sectionId: string, columnId: string) => void;
    onChecked?: (selection: AscsCheckedSelection | undefined) => void;
    preselectedPath?: AscsPreselectedPath;
    selectionDisabled?: boolean;
    maxLevel?: RowTypes;
}


const TreeRow = (props: {
    id: string;
    title: string;
    count: number;
    selectedId: string | undefined;
    loading?: boolean;
    isOpen?: boolean;
    isRoot?: boolean;
    isLocked?: boolean;
    selectionDisabled?: boolean;
    post?: PostDownloadDto;
    onTitleClick: () => void;
    onCheck: () => void;
    type?: RowTypes;
}) => {
    const { id, title, count, selectedId, loading, isOpen, isRoot, isLocked, selectionDisabled, post, onTitleClick, onCheck, type } = props;

    const iconName: ImgIcons = isLocked && isOpen ? "folder up" : isOpen ? "folder open" : count === 0 ? "folder empty" : "folder";
    const showCount = !isLocked && !isOpen && count !== 0; // for open locked categories, the count is not meaningful, so we hide it
    const iconColor = (isLocked && isOpen) ? "@accentRed" : "@lightGrey";
    return (
        <Outer active={selectedId === id} isRoot={isRoot} onClick={e => { if (e.detail === 2 && !selectionDisabled) onCheck(); }}>
            <Title selected={selectedId === id} onClick={e => { if (e.detail !== 2 && count !== 0) onTitleClick(); }}>
                {type !== 'cat' &&
                    <TypeBox left={-CONNECTOR_W}>
                        {type}
                    </TypeBox>
                }
                <Icon
                    name={iconName}
                    style={{ flexShrink: 0, transform: "scale(1.1)", marginLeft: -8 }}
                    color={iconColor}
                />
                {showCount && <div style={{ zIndex: 10, fontSize: 9, position: "absolute", left: -5, top: 9, width: 14, height: 10, padding: 0, lineHeight: "10px", textAlign: "center" }}>{count}</div>}

                {title}
            </Title>
            <Infos loading={!!loading} onClick={e => e.stopPropagation()}>
                {post && <div style={{ position: "absolute", right: 20, top: -20, zIndex: 5 }}><PlaylistEditPreviewElement post={post} /></div>}
                {loading
                    ? <LottieIcon url="images/lottiIcons/spinner.json" speed={1} autoplay loop height={17} width={17} />
                    : <div>
                        {/* <Info>
                            <div onClick={selectionDisabled ? undefined : onCheck}>{type}</div>
                            <div onClick={selectionDisabled ? undefined : onCheck}>{count === 0 ? "-" : count}</div>
                            <SwitchButton initialState={selectedId === id} isActive={onCheck} size='small' floated='right' disabled={selectionDisabled} />
                        </Info> */}
                        <SwitchButton initialState={selectedId === id} isActive={onCheck} size='small' floated='right' disabled={selectionDisabled} />
                    </div>
                }
            </Infos>
        </Outer>
    );
};

const AscsTreeView: React.FC<AscsTreeViewProps> = (p) => {
    const {
        categories, articles, articleDetailsCache, loadingArticleIds, loadingSubcatIds,
        contentLanguage, selectSection, selectColumns, t,
        onShowSubcats, onLoadSubcatArticles, onLoadSubcatWithSections,
        onShowArticle, onSectionSelected, onColumnSelected,
        preselectedPath, selectionDisabled, maxLevel,
    } = p;

    // maxIdx: how deep the tree can go (0=cat,1=sub,2=art,3=sec,4=col)
    // Falls back to inferring from selectSection/selectColumns for backward compat.
    const maxIdx = LEVEL_ORDER.indexOf(maxLevel ?? (selectColumns ? 'col' : selectSection ? 'sec' : 'art'));

    const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
    const [selectedId, setSelectedId] = React.useState<string | undefined>();
    const [lockedIds, setLockedIds] = React.useState<Set<string>>(new Set());

    React.useEffect(() => {
        // Determine the deepest selected id and which ancestors to expand
        const selId =
            preselectedPath?.columnId ??
            preselectedPath?.sectionId ??
            preselectedPath?.articleId ??
            preselectedPath?.subCategoryId ??
            preselectedPath?.categoryId;
        if (!selId) return;

        const ids = new Set<string>();
        // Expand ancestors so the selected item is visible:
        //   column  → expand catId, subCatId, articleId, sectionId
        //   section → expand catId, subCatId, articleId
        //   article → expand catId, subCatId
        //   subcat  → expand catId
        //   cat     → nothing to expand (it's a root row)
        if (preselectedPath?.articleId) {
            if (preselectedPath.categoryId) ids.add(preselectedPath.categoryId);
            if (preselectedPath.subCategoryId) ids.add(preselectedPath.subCategoryId);
            if (preselectedPath.sectionId) {
                ids.add(preselectedPath.articleId);
                if (preselectedPath.columnId) ids.add(preselectedPath.sectionId);
            }
        } else if (preselectedPath?.subCategoryId) {
            if (preselectedPath.categoryId) ids.add(preselectedPath.categoryId);
        }

        setExpandedIds(ids);
        setSelectedId(selId);
        const locked = new Set(ids);
        locked.delete(selId);
        setLockedIds(locked);
    }, [
        preselectedPath?.categoryId,
        preselectedPath?.subCategoryId,
        preselectedPath?.articleId,
        preselectedPath?.sectionId,
        preselectedPath?.columnId,
    ]);

    const toggle = (id: string) => {
        // Do not collapse an ancestor of the currently selected item
        if (lockedIds.has(id) && expandedIds.has(id)) return;
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleCheck = (id: string, context: AscsCheckedSelection | undefined) => {
        const newVal = selectedId === id ? undefined : id;
        setSelectedId(newVal);
        if (newVal !== undefined && context) {
            const locked = new Set<string>();
            if (context.categoryId) locked.add(context.categoryId);
            if (context.subCategoryId) locked.add(context.subCategoryId);
            if (context.articleId) locked.add(context.articleId);
            if (context.sectionId) locked.add(context.sectionId);
            locked.delete(id); // the selected item itself is not locked
            setLockedIds(locked);
        } else {
            setLockedIds(new Set());
        }
        p.onChecked?.(newVal !== undefined ? context : undefined);
    };

    const renderColumns = (
        articleId: string,
        sectionId: string,
        section: NonNullable<WikiSearchResultDto['sections']>[number],
        catId?: string,
        subCatId?: string,
    ) => _.map(section.columns, c => {
        const colTitle = getRidOfHtml(c.title) || t('no title');
        const cId = c.id ?? '';
        return (
            <TreeRow
                key={c.id}
                id={cId}
                title={colTitle}
                count={0}
                selectedId={selectedId}
                isOpen={false}
                isLocked={lockedIds.has(cId)}
                selectionDisabled={selectionDisabled}
                onTitleClick={() => { if (selectColumns) onColumnSelected(articleId, sectionId, cId); }}
                onCheck={() => handleCheck(cId, { articleId, sectionId, columnId: cId, categoryId: catId, subCategoryId: subCatId })}
                type='col'
            />
        );
    });

    const renderSections = (articleId: string, catId?: string, subCatId?: string) => {
        const articleData = articleDetailsCache[articleId];
        if (!articleData) return null;
        return _.map(articleData.sections, s => {
            const secTitle = getRidOfHtml(s.title) || t('no title');
            const colCount = s.columns?.length ?? 0;
            const sId = s.id ?? '';
            const isSecOpen = expandedIds.has(sId);
            return (
                <React.Fragment key={s.id}>
                    <TreeRow
                        id={sId}
                        title={secTitle}
                        count={colCount}
                        selectedId={selectedId}
                        isOpen={isSecOpen}
                        onTitleClick={() => {
                            if (maxIdx > 3) toggle(sId);
                            if (selectSection) onSectionSelected(articleId, sId);
                        }}
                        isLocked={lockedIds.has(sId)}
                        selectionDisabled={selectionDisabled}
                        onCheck={() => handleCheck(sId, { articleId, sectionId: sId, categoryId: catId, subCategoryId: subCatId })}
                        type='sec'
                    />
                    {isSecOpen && maxIdx > 3 && <Branch>{renderColumns(articleId, sId, s, catId, subCatId)}</Branch>}
                </React.Fragment>
            );
        });
    };

    const renderArticles = (articleList: WikiArticleDownloadDto[], catId?: string, subCatId?: string) =>
        _.map(articleList, article => {
            const id = article.id ?? '';
            const isLoading = loadingArticleIds.has(id);
            const cached = articleDetailsCache[id];
            const secCount = cached?.sections?.length ?? 0;
            const isOpen = expandedIds.has(id);
            return (
                <React.Fragment key={id}>
                    <TreeRow
                        id={id}
                        title={getTranslated(article.headlines, contentLanguage).text}
                        count={secCount}
                        loading={isLoading}
                        selectedId={selectedId}
                        isOpen={isOpen}
                        post={article as PostDownloadDto}
                        onTitleClick={() => {
                            if (maxIdx > 2) {
                                if (!isOpen && !cached && !isLoading) onShowArticle(article);
                                toggle(id);
                            }
                        }}
                        isLocked={lockedIds.has(id)}
                        selectionDisabled={selectionDisabled}
                        onCheck={() => handleCheck(id, { articleId: id, categoryId: catId, subCategoryId: subCatId })}
                        type='art'
                    />
                    {isOpen && maxIdx > 2 && <Branch>{renderSections(id, catId, subCatId)}</Branch>}
                </React.Fragment>
            );
        });

    return (
        <div>
            {_.map(categories, cat => {
                const hasChildren = !!(cat.children && cat.children.length > 0);
                const directArticles = articles[cat.id]?.[cat.id];
                const catCount = hasChildren ? cat.children!.length : (directArticles?.length ?? 0);
                const isOpen = expandedIds.has(cat.id);

                return (
                    <React.Fragment key={cat.id}>
                        <TreeRow
                            isRoot
                            id={cat.id}
                            title={getTranslated(cat.names, contentLanguage).text}
                            count={catCount}
                            selectedId={selectedId}
                            isOpen={isOpen}
                            onTitleClick={() => {
                                if (maxIdx > 0) {
                                    if (!isOpen) {
                                        if (hasChildren) onLoadSubcatArticles(cat);
                                        else onShowSubcats(cat.id);
                                    }
                                    toggle(cat.id);
                                }
                            }}
                            isLocked={lockedIds.has(cat.id)}
                            selectionDisabled={selectionDisabled}
                            onCheck={() => handleCheck(cat.id, { categoryId: cat.id })}
                            type='cat'
                        />
                        {isOpen && maxIdx > 0 && (
                            <Branch>
                                {hasChildren
                                    ? _.map(cat.children, subCat => {
                                        const subCatArticles = articles[subCat.id]
                                            ? _.flatMap(Object.values(articles[subCat.id]))
                                            : undefined;
                                        const artCount = subCatArticles?.length ?? 0;
                                        const isSubOpen = expandedIds.has(subCat.id);
                                        return (
                                            <React.Fragment key={subCat.id}>
                                                <TreeRow
                                                    id={subCat.id}
                                                    title={getTranslated(subCat.names, contentLanguage).text}
                                                    count={artCount}
                                                    loading={loadingSubcatIds.has(subCat.id)}
                                                    selectedId={selectedId}
                                                    isOpen={isSubOpen}
                                                    onTitleClick={() => {
                                                        if (maxIdx > 1) {
                                                            if (!isSubOpen) onLoadSubcatWithSections(subCat);
                                                            toggle(subCat.id);
                                                        }
                                                    }}
                                                    isLocked={lockedIds.has(subCat.id)}
                                                    selectionDisabled={selectionDisabled}
                                                    onCheck={() => handleCheck(subCat.id, { categoryId: cat.id, subCategoryId: subCat.id })}
                                                    type='sub'
                                                />
                                                {isSubOpen && maxIdx > 1 && (
                                                    <Branch>
                                                        {renderArticles(subCatArticles ?? [], cat.id, subCat.id)}
                                                    </Branch>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                    : maxIdx > 1 ? renderArticles(directArticles ?? [], cat.id) : null
                                }
                            </Branch>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default AscsTreeView;
